import React, { useState, useEffect } from 'react';
import { useNav } from '../App';
import { Box, Typography, Paper, Button, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, Checkbox, FormGroup, Stepper, Step, StepLabel, CircularProgress } from '@mui/material';
import axios from 'axios';
import { C } from '../theme';

function CheckInForm() {
  const navigate = useNav();
  const [step, setStep] = useState(1);
  const [isFirstTime, setIsFirstTime] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  
  const [hairType, setHairType] = useState('');
  const [facialHair, setFacialHair] = useState([]);
  const [hasGlasses, setHasGlasses] = useState('');
  
  const [wearingGlassesNow, setWearingGlassesNow] = useState(false);
  const [locationArea, setLocationArea] = useState('');
  const [clothing, setClothing] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [groupStatus, setGroupStatus] = useState('');

  const venueId = new URLSearchParams(window.location.search).get('venueId');
  const venueName = new URLSearchParams(window.location.search).get('venueName');

  useEffect(() => {
    checkIfFirstTime();
  }, []);

  const checkIfFirstTime = async () => {
    try {
      const token = localStorage.getItem('binko_token');
      const response = await axios.get((process.env.REACT_APP_API_BASE || 'https://api.binko.me/api') + '/auth/me', {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      const user = response.data.user;
      setUserProfile(user);
      
      if (!user.hair_type) {
        setIsFirstTime(true);
        setStep(1);
      } else {
        setIsFirstTime(false);
        setStep(2);
        setHairType(user.hair_type);
        setHasGlasses(user.has_glasses ? 'yes' : 'no');
        setFacialHair(user.facial_hair === 'Both' ? ['Beard', 'Mustache'] : 
                      user.facial_hair === 'None' ? [] : [user.facial_hair]);
      }
    } catch (error) {
      console.error('Failed to check user profile');
    }
    setLoading(false);
  };

  const handleFacialHairChange = (value) => {
    if (facialHair.includes(value)) {
      setFacialHair(facialHair.filter(item => item !== value));
    } else {
      setFacialHair([...facialHair, value]);
    }
  };

  const handleClothingChange = (value) => {
    if (clothing.includes(value)) {
      setClothing(clothing.filter(item => item !== value));
    } else if (clothing.length < 2) {
      setClothing([...clothing, value]);
    }
  };

  const handleAccessoriesChange = (value) => {
    if (accessories.includes(value)) {
      setAccessories(accessories.filter(item => item !== value));
    } else {
      setAccessories([...accessories, value]);
    }
  };

  const handleStep1Continue = async () => {
    if (!hairType || !hasGlasses) {
      alert('Please answer all questions');
      return;
    }

    const facialHairValue = facialHair.length === 0 ? 'None' : 
                           facialHair.length === 2 ? 'Both' : facialHair[0];

    try {
      const token = localStorage.getItem('binko_token');
      await axios.post((process.env.REACT_APP_API_BASE || 'https://api.binko.me/api') + '/auth/update-identifiers', {
        hairType,
        facialHair: facialHairValue,
        hasGlasses: hasGlasses === 'yes'
      }, {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      setStep(2);
    } catch (error) {
      alert('Failed to save identifiers');
    }
  };

  const handleStep2Continue = () => {
    if (!locationArea || clothing.length === 0 || !groupStatus) {
      alert('Please fill in all required fields');
      return;
    }
    setStep(3);
  };

  const handleCheckIn = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('binko_token');
      await axios.post((process.env.REACT_APP_API_BASE || 'https://api.binko.me/api') + '/checkin', {
        venueId,
        wearingGlasses: hasGlasses === 'yes' ? wearingGlassesNow : false,
        locationArea,
        clothingPrimary: clothing[0],
        clothingSecondary: clothing[1] || null,
        accessories: accessories.join(', '),
        groupStatus
      }, {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      navigate('/home');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to check in');
    }
  };



  if (loading) return <Box sx={{ p: 3 }}>Loading...</Box>;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: C.bgApp, p: 3 }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
        {isFirstTime && (
          <Stepper activeStep={step - 1} sx={{ mb: 3 }}>
            <Step><StepLabel>Profile Setup</StepLabel></Step>
            <Step><StepLabel>Session Info</StepLabel></Step>
            <Step><StepLabel>Preview</StepLabel></Step>
          </Stepper>
        )}

        {step === 1 && (
          <>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>First Time Setup</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              This info will be shown when you check into venues
            </Typography>

            <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
              <FormLabel sx={{ fontWeight: 'bold', mb: 1 }}>Hair:</FormLabel>
              <RadioGroup value={hairType} onChange={(e) => setHairType(e.target.value)}>
                <FormControlLabel value="Long hair" control={<Radio />} label="Long hair" />
                <FormControlLabel value="Short hair" control={<Radio />} label="Short hair" />
                <FormControlLabel value="Bald/shaved" control={<Radio />} label="Bald / shaved" />
                <FormControlLabel value="Tied up" control={<Radio />} label="Tied up" />
              </RadioGroup>
            </FormControl>

            <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
              <FormLabel sx={{ fontWeight: 'bold', mb: 1 }}>Facial hair (if applicable):</FormLabel>
              <FormGroup>
                <FormControlLabel control={<Checkbox checked={facialHair.includes('Beard')} onChange={() => handleFacialHairChange('Beard')} />} label="Beard" />
                <FormControlLabel control={<Checkbox checked={facialHair.includes('Mustache')} onChange={() => handleFacialHairChange('Mustache')} />} label="Mustache" />
              </FormGroup>
            </FormControl>

            <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
              <FormLabel sx={{ fontWeight: 'bold', mb: 1 }}>Do you wear glasses?</FormLabel>
              <RadioGroup value={hasGlasses} onChange={(e) => setHasGlasses(e.target.value)}>
                <FormControlLabel value="yes" control={<Radio />} label="Usually wear glasses" />
                <FormControlLabel value="no" control={<Radio />} label="Don't wear glasses" />
              </RadioGroup>
            </FormControl>

            <Button variant="contained" fullWidth onClick={handleStep1Continue}>Continue</Button>
          </>
        )}

        {step === 2 && (
          <>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>Help Others Recognize You</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Where are you at {venueName}?
            </Typography>

            {hasGlasses === 'yes' && (
              <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                <FormLabel sx={{ fontWeight: 'bold', mb: 1 }}>Wearing glasses now?</FormLabel>
                <FormGroup>
                  <FormControlLabel control={<Checkbox checked={wearingGlassesNow} onChange={(e) => setWearingGlassesNow(e.target.checked)} />} label="Yes, wearing them" />
                </FormGroup>
              </FormControl>
            )}

            <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
              <FormLabel sx={{ fontWeight: 'bold', mb: 1 }}>Where are you?</FormLabel>
              <RadioGroup value={locationArea} onChange={(e) => setLocationArea(e.target.value)}>
                <FormControlLabel value="At the bar" control={<Radio />} label="At the bar" />
                <FormControlLabel value="Tables area" control={<Radio />} label="Tables area" />
                <FormControlLabel value="Lounge/couches" control={<Radio />} label="Lounge / couches" />
                <FormControlLabel value="Near entrance" control={<Radio />} label="Near entrance" />
                <FormControlLabel value="Outdoor area" control={<Radio />} label="Outdoor area" />
                <FormControlLabel value="Standing/moving" control={<Radio />} label="Standing / moving around" />
              </RadioGroup>
            </FormControl>

            <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
              <FormLabel sx={{ fontWeight: 'bold', mb: 1 }}>Clothing (pick 1-2):</FormLabel>
              <FormGroup>
                <FormControlLabel control={<Checkbox checked={clothing.includes('Dark top')} onChange={() => handleClothingChange('Dark top')} />} label="Dark top" />
                <FormControlLabel control={<Checkbox checked={clothing.includes('Light top')} onChange={() => handleClothingChange('Light top')} />} label="Light top" />
                <FormControlLabel control={<Checkbox checked={clothing.includes('Bright colors')} onChange={() => handleClothingChange('Bright colors')} />} label="Bright colors" />
                <FormControlLabel control={<Checkbox checked={clothing.includes('Dress')} onChange={() => handleClothingChange('Dress')} />} label="Dress" />
                <FormControlLabel control={<Checkbox checked={clothing.includes('Suit/blazer')} onChange={() => handleClothingChange('Suit/blazer')} />} label="Suit / blazer" />
                <FormControlLabel control={<Checkbox checked={clothing.includes('Hoodie')} onChange={() => handleClothingChange('Hoodie')} />} label="Hoodie" />
                <FormControlLabel control={<Checkbox checked={clothing.includes('Jacket')} onChange={() => handleClothingChange('Jacket')} />} label="Jacket" />
              </FormGroup>
              <Typography variant="caption" color="text.secondary">Selected: {clothing.length}/2</Typography>
            </FormControl>

            <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
              <FormLabel sx={{ fontWeight: 'bold', mb: 1 }}>What are you carrying? (optional)</FormLabel>
              <FormGroup>
                <FormControlLabel control={<Checkbox checked={accessories.includes('Cap/hat')} onChange={() => handleAccessoriesChange('Cap/hat')} />} label="Cap / hat" />
                <FormControlLabel control={<Checkbox checked={accessories.includes('Backpack')} onChange={() => handleAccessoriesChange('Backpack')} />} label="Backpack" />
                <FormControlLabel control={<Checkbox checked={accessories.includes('Handbag')} onChange={() => handleAccessoriesChange('Handbag')} />} label="Handbag" />
                <FormControlLabel control={<Checkbox checked={accessories.includes('Nothing noticeable')} onChange={() => handleAccessoriesChange('Nothing noticeable')} />} label="Nothing noticeable" />
              </FormGroup>
            </FormControl>

            <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
              <FormLabel sx={{ fontWeight: 'bold', mb: 1 }}>You're here...</FormLabel>
              <RadioGroup value={groupStatus} onChange={(e) => setGroupStatus(e.target.value)}>
                <FormControlLabel value="Solo" control={<Radio />} label="Solo" />
                <FormControlLabel value="With 1-2 friends" control={<Radio />} label="With 1-2 friends" />
                <FormControlLabel value="With a group" control={<Radio />} label="With a group" />
              </RadioGroup>
            </FormControl>

            <Button variant="contained" fullWidth onClick={handleStep2Continue}>Continue to Preview</Button>
          </>
        )}

        {step === 3 && (
          <>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>How You'll Appear</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              This is what others will see when browsing
            </Typography>

            <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: C.bgCard }}>
              <Typography variant="h6" gutterBottom>Preview:</Typography>
              <Box sx={{ mb: 2 }}>
                
<Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                  {userProfile?.alias || 'Your Alias'}, {userProfile?.age_group || ''}
                </Typography>


          </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">📍 {locationArea}</Typography>
                <Typography variant="body2">👕 {clothing.join(', ')}</Typography>
                <Typography variant="body2">
                  ✨ {hairType}
                  {facialHair.length > 0 && `, ${facialHair.join(' & ')}`}
                  {(hasGlasses === 'yes' && wearingGlassesNow) && ', Glasses'}
                </Typography>
                {accessories.length > 0 && accessories[0] !== 'Nothing noticeable' && (
                  <Typography variant="body2">🎒 {accessories.join(', ')}</Typography>
                )}
                <Typography variant="body2">👥 {groupStatus}</Typography>
              </Box>
            </Paper>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={() => setStep(2)} fullWidth>Edit This</Button>
              <Button variant="contained" onClick={handleCheckIn} fullWidth disabled={submitting}>
                {submitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Check In ✓'}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}

export default CheckInForm;
